import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import Notification from '../components/Notification';
import { API_BASE_URL } from '../config';
import { getBrusselsTodayIndex, hasKnownOpenDays, normalizeTodayStatus } from '../MachineManager/openDays';

export const MachineContext = createContext({
  machines: [],
  optionalFeatures: [],
  bonusConfiguration: [],
  longTrackPriceModes: [],
  paymentModes: [],
  diskPolicies: [],
  loading: false,
  error: null,
  fetchMachines: async () => {},
  fetchOptionalFeatures: async () => {},
  fetchBonusConfiguration: async () => {},
  fetchLongTrackPriceModes: async () => {},
  fetchPaymentModes: async () => {},
  fetchDiskPolicies: async () => {},
  createMachine: async () => {},
  updateMachine: async () => {},
  updateMachineWorkdays: async () => null,
  updateMachineGeodata: async () => null,
  geocodeMachine: async () => null,
  updateOptionalFeatures: async () => {},
  updateOvereenkomst: async () => {},
  updateDeactivateType: async () => {},
  updateBonusConfiguration: async () => {},
  updateLongTrackPriceMode: async () => {},
  updatePaymentMode: async () => {},
  updateDiskPolicy: async () => {},
  fetchOptionalFeatureAggregation: async () => [],
  bulkApplyOptionalFeature: async () => null,
  bulkResetTokens: async () => null,
  bulkGiftTokens: async () => null,
  toggleMachinePlaylist: async () => {},
  updatePlaylistPriority: async () => {},
  showSuccessNotification: () => {},
  showErrorNotification: () => {},
  fetchCreditRequests: async () => {},
  fetchTokenBalance: async () => {},
  resetTokens: async () => {},
  addTokens: async () => {},
  fetchTokenConfigs: async () => {},
  updateTokenConfig: async () => {},
  clearBillingPeriod: async () => {},
});

// DJB-258: GET /machines/workdays answers a list of MachineWorkdaysDTO
// { machineId, openDays: null | 7 booleans (Ma → Zo), todayStatus }. Indexed
// by machine id so the overview rows can pick theirs up; a paged wrapper
// ({ content: [...] }) is unwrapped, anything else reads as an empty list.
export const toWorkdaysByMachineId = (payload) => {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.content)
      ? payload.content
      : [];
  const byMachineId = {};
  rows.forEach((row) => {
    if (!row || row.machineId === undefined || row.machineId === null) return;
    byMachineId[String(row.machineId)] = {
      machineId: row.machineId,
      openDays: hasKnownOpenDays(row.openDays) ? row.openDays.map(Boolean) : null,
      todayStatus: normalizeTodayStatus(row.todayStatus),
    };
  });
  return byMachineId;
};

// DJB-258: machines with a workdays row get its openDays + todayStatus; the
// others are left as they came in (which normalises to unknown). A null map
// (the bulk fetch failed) changes nothing.
export const mergeWorkdays = (machines, workdaysByMachineId) => {
  if (!workdaysByMachineId) return machines;
  return machines.map((machine) => {
    const workdays = workdaysByMachineId[String(machine.id)];
    return workdays
      ? { ...machine, openDays: workdays.openDays, todayStatus: workdays.todayStatus }
      : machine;
  });
};

const MachineProvider = ({ children }) => {
  const { authData } = useContext(AuthContext);
  const [machines, setMachines] = useState([]);
  const [optionalFeatures, setOptionalFeatures] = useState([]);
  const [bonusConfiguration, setBonusConfiguration] = useState([]);
  const [longTrackPriceModes, setLongTrackPriceModes] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [diskPolicies, setDiskPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
  };

  const showSuccessNotification = (message) => {
    addNotification(message, 'success');
  };

  const showErrorNotification = (message) => {
    addNotification(message, 'error');
  };

  const arrayToDate = (dateArray) => {
    // dateArray format: [year, month, day, hour, minute, second, nanoseconds]
    return new Date(
      dateArray[0],     // year
      dateArray[1] - 1, // month (0-based)
      dateArray[2],     // day
      dateArray[3],     // hour
      dateArray[4],     // minute
      dateArray[5],     // second
      dateArray[6] / 1000000 // convert nanoseconds to milliseconds
    );
  };

  const fetchBillingHistory = async (machine) => {
    try {
      const billingResponse = await fetch(
        `${API_BASE_URL}/machines/${machine.id}/billing/`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!billingResponse.ok) {
        throw new Error('Failed to fetch billing data');
      }

      const billingData = await billingResponse.json();
      
      // Transform the billing data
      const financialHistory = billingData.map(entry => ({
        cashoutAt: arrayToDate(entry.cashoutAt),
        previousCashoutAt: arrayToDate(entry.previousCashoutAt),
        incomeFromCoin: entry.incomeFromCoin,
        incomeFromPayconiq: entry.incomeFromPayconiq,
        songsRequested: entry.songsRequested
      }));

      return { ...machine, financialHistory };
    } catch (error) {
      console.error(`Failed to fetch billing history for machine ${machine.id}:`, error);
      return { ...machine, financialHistory: [] };
    }
  };

  const fetchBlacklist = async (machine) => {
    try {
      const blacklistResponse = await fetch(
        `${API_BASE_URL}/machines/${machine.id}/blacklists/`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!blacklistResponse.ok) {
        throw new Error('Failed to fetch blacklist data');
      }

      const blacklistData = await blacklistResponse.json();
      return { ...machine, blacklist: blacklistData };
    } catch (error) {
      console.error(`Failed to fetch blacklist for machine ${machine.id}:`, error);
      return { ...machine, blacklist: [] }; // Return empty blacklist on error
    }
  };

  const fetchMachines = async () => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping fetch.");
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      console.log("Fetching machines...");
      const response = await fetch(
        `${API_BASE_URL}/machines/?page=0&size=1000`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch machines');
      }

      let machinesData = await response.json();
      
      // Fetch playlist subscriptions via the V2 endpoint. `playlists` stays a
      // plain id array (existing consumers rely on it, e.g. MachineDetails);
      // the per-playlist `prioritized` flag is kept alongside in
      // `playlistPriorities` (playlistId -> boolean).
      const fetchPlaylists = async (machine) => {
        try {
          const playlistResponse = await fetch(
            `${API_BASE_URL}/v2/machines/${machine.id}/playlists`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${authData.accessToken}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (!playlistResponse.ok) {
            throw new Error('Failed to fetch playlists data');
          }

          const subscriptions = await playlistResponse.json();
          const playlistPriorities = {};
          subscriptions.forEach((subscription) => {
            playlistPriorities[subscription.playlistId] = subscription.prioritized === true;
          });
          return {
            ...machine,
            playlists: subscriptions.map((subscription) => subscription.playlistId),
            playlistPriorities,
          };
        } catch (error) {
          console.error(`Failed to fetch playlists for machine ${machine.id}:`, error);
          return { ...machine, playlists: [], playlistPriorities: {} }; // Return empty playlists on error
        }
      };

      // Modify the existing fetchAdministration to include playlists and billing history
      const fetchAdministration = async (machine) => {
        try {
          const adminResponse = await fetch(
            `${API_BASE_URL}/machines/${machine.id}/administration`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${authData.accessToken}`,
                'Content-Type': 'application/json',
              },
            }
          );
          
          if (!adminResponse.ok) {
            throw new Error('Failed to fetch administration data');
          }
          
          const adminData = await adminResponse.json();
          delete adminData.id;

          // Add admin data
          let machineWithAdmin = { ...machine, ...adminData };
          // Fetch playlists
          let machineWithPlaylists = await fetchPlaylists(machineWithAdmin);
          // Fetch blacklist
          let machineWithBlacklist = await fetchBlacklist(machineWithPlaylists);
          // Fetch billing history
          return await fetchBillingHistory(machineWithBlacklist);
        } catch (error) {
          console.error(`Failed to fetch administration for machine ${machine.id}:`, error);
          // Continue with playlists, blacklist, and billing even if admin fails
          let machineWithPlaylists = await fetchPlaylists(machine);
          let machineWithBlacklist = await fetchBlacklist(machineWithPlaylists);
          return await fetchBillingHistory(machineWithBlacklist);
        }
      };
      
      const concurrencyLimit = 10;
      const fetchWithConcurrency = async (machines) => {
        const results = [];
        for (let i = 0; i < machines.length; i += concurrencyLimit) {
          const batch = machines.slice(i, i + concurrencyLimit);
          const fetchedBatch = await Promise.all(batch.map(fetchAdministration));
          results.push(...fetchedBatch);
        }
        return results;
      };

      // DJB-258: neither /machines/ nor /administration carries the open
      // days (DJB-252 moved them to their own resource), so without this the
      // info-card dots and the Today column only held up until a reload. The
      // bulk endpoint answers one MachineWorkdaysDTO per machine; a failure
      // just leaves the days unknown rather than blocking the whole load.
      const fetchWorkdays = async () => {
        try {
          const workdaysResponse = await fetch(`${API_BASE_URL}/machines/workdays`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            },
          });
          if (!workdaysResponse.ok) {
            throw new Error('Failed to fetch workdays');
          }
          return toWorkdaysByMachineId(await workdaysResponse.json());
        } catch (error) {
          console.error('Failed to fetch machine workdays:', error);
          return null;
        }
      };

      const [machinesWithAdmin, workdaysByMachineId] = await Promise.all([
        fetchWithConcurrency(machinesData),
        fetchWorkdays(),
      ]);
      machinesData = mergeWorkdays(machinesWithAdmin, workdaysByMachineId);
      setMachines(machinesData);
    } catch (error) {
      setError(error.message);
      showErrorNotification(`Error fetching machines: ${error.message}`);
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  // DJB-214: `notify: false` lets the Playlist Subscriptions bulk run show a
  // progress bar and one aggregated toast instead of a toast per machine
  const toggleMachinePlaylist = async (machineId, playlistId, isSubscribing, { notify = true } = {}) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping update.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/playlists/${playlistId}`,
        {
          method: isSubscribing ? 'POST' : 'DELETE',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${isSubscribing ? 'subscribe to' : 'unsubscribe from'} playlist`);
      }

      // Update the local state
      let updatedMachine = null;
      setMachines(prevMachines => {
        const newMachines = prevMachines.map(machine => {
          if (machine.id === machineId) {
            const newPlaylists = isSubscribing
              ? [...machine.playlists, playlistId]
              : machine.playlists.filter(id => id !== playlistId);
            // Keep the priorities map in sync: new subscriptions start
            // unprioritized, removed ones drop their entry.
            const newPriorities = { ...machine.playlistPriorities };
            if (isSubscribing) {
              newPriorities[playlistId] = false;
            } else {
              delete newPriorities[playlistId];
            }
            updatedMachine = { ...machine, playlists: newPlaylists, playlistPriorities: newPriorities };
            return updatedMachine;
          }
          return machine;
        });
        return newMachines;
      });

      if (notify) {
        showSuccessNotification(
          `Successfully ${isSubscribing ? 'subscribed to' : 'unsubscribed from'} playlist`
        );
      }

      // Return the updated machine
      return updatedMachine;
    } catch (error) {
      if (notify) showErrorNotification(error.message);
      throw error;
    }
  };

  const updatePlaylistPriority = async (machineId, playlistId, prioritized) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping update.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/v2/machines/${machineId}/playlists/${playlistId}/priority`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ prioritized }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update playlist priority');
      }

      setMachines(prevMachines =>
        prevMachines.map(machine =>
          machine.id === machineId
            ? {
                ...machine,
                playlistPriorities: { ...machine.playlistPriorities, [playlistId]: prioritized },
              }
            : machine
        )
      );

      showSuccessNotification(
        prioritized ? 'Playlist prioritized for machine' : 'Playlist priority removed for machine'
      );
    } catch (error) {
      showErrorNotification(`Error updating playlist priority: ${error.message}`);
      throw error;
    }
  };

  const fetchCreditRequests = async (machineId, numberOfDays) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping fetch.");
      return [];
    }

    try {
      // Calculate the from date
      const today = new Date();
      const fromDate = new Date(today);
      fromDate.setDate(today.getDate() - numberOfDays);
      
      // Format date to yyyy-MM-dd
      const formattedDate = fromDate.toISOString().split('T')[0];

      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/credit-requests?from=${formattedDate}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch credit requests');
      }

      const creditRequests = await response.json();
      return creditRequests;

    } catch (error) {
      showErrorNotification(`Failed to fetch credit requests: ${error.message}`);
      throw error;
    }
  };

  const fetchOptionalFeatures = async () => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping fetch.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/optional-features-bundled`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch optional features');
      }

      const optionalFeaturesData = await response.json();
      setOptionalFeatures(optionalFeaturesData);
    } catch (error) {
      console.error("Failed to fetch optional features:", error);
      setOptionalFeatures([]);
    }
  };

  const fetchBonusConfiguration = async () => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping fetch.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/bonusconfiguration`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch bonus configuration');
      }

      const bonusConfigurationData = await response.json();
      setBonusConfiguration(bonusConfigurationData);
    } catch (error) {
      console.error("Failed to fetch bonus configuration:", error);
      setBonusConfiguration([]);
    }
  };

  const fetchLongTrackPriceModes = async () => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping fetch.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/longtrackpricemodes`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch long track price modes');
      }

      const longTrackPriceModesData = await response.json();
      setLongTrackPriceModes(longTrackPriceModesData);
    } catch (error) {
      console.error("Failed to fetch long track price modes:", error);
      setLongTrackPriceModes([]);
    }
  };

  // Like /longtrackpricemodes, /paymentmodes returns headers only ({id, name})
  const fetchPaymentModes = async () => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping fetch.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/paymentmodes`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch payment modes');
      }

      const paymentModesData = await response.json();
      setPaymentModes(paymentModesData);
    } catch (error) {
      console.error("Failed to fetch payment modes:", error);
      setPaymentModes([]);
    }
  };

  const fetchDiskPolicies = async () => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping fetch.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/disk-policies`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch disk policies');
      }

      const diskPoliciesData = await response.json();
      setDiskPolicies(diskPoliciesData);
    } catch (error) {
      console.error("Failed to fetch disk policies:", error);
      setDiskPolicies([]);
    }
  };

  const updateOptionalFeatures = async (machineId, optionalFeaturesData) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping update.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/optional-features`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(optionalFeaturesData)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update optional features');
      }

      const updatedOptionalFeatures = await response.json();
      
      // Update the local state - replace if exists, add if new
      setOptionalFeatures(prevFeatures => {
        const existingIndex = prevFeatures.findIndex(feature => feature.machineId === machineId);
        if (existingIndex >= 0) {
          // Replace existing
          const newFeatures = [...prevFeatures];
          newFeatures[existingIndex] = updatedOptionalFeatures;
          return newFeatures;
        } else {
          // Add new
          return [...prevFeatures, updatedOptionalFeatures];
        }
      });

      showSuccessNotification('Optional features updated successfully.');
      return updatedOptionalFeatures;
    } catch (error) {
      showErrorNotification(`Error updating optional features: ${error.message}`);
      throw error;
    }
  };

  const createMachine = async (machineData) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping create.");
      return;
    }

    try {
      // The machine id (UUID) is generated backend-side, so it is not sent.
      const machineBody = {
        name: machineData.name,
        type: machineData.type,
        province: machineData.province,
        city: machineData.city,
        street: machineData.street,
        postalcode: machineData.postalcode,
        housenumber: machineData.housenumber,
        maxTracksOnMachine: machineData.maxTracksOnMachine,
        amountTokens: machineData.amountTokens,
        overeenkomst: machineData.overeenkomst,
      };

      const response = await fetch(
        `${API_BASE_URL}/machines/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(machineBody)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create machine');
      }

      const createdMachine = await response.json();

      showSuccessNotification('Machine created successfully.');
      // Refresh the full machine list so the new account appears everywhere.
      await fetchMachines();
      return createdMachine;
    } catch (error) {
      showErrorNotification(`Error creating machine: ${error.message}`);
      throw error;
    }
  };

  const updateMachine = async (machineId, machineData) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping update.");
      return;
    }

    try {
      // Send only the machine DTO fields (the cached machine also holds
      // client-only data merged in on fetch: playlists, blacklist, etc.).
      const machineBody = {
        id: machineData.id,
        name: machineData.name,
        province: machineData.province,
        city: machineData.city,
        street: machineData.street,
        housenumber: machineData.housenumber,
        postalcode: machineData.postalcode,
        type: machineData.type,
        lastSeen: machineData.lastSeen,
        maxTracksOnMachine: machineData.maxTracksOnMachine,
        firstSeen: machineData.firstSeen,
        overeenkomst: machineData.overeenkomst,
        // DJB-252 / DJB-Server PR #80: openDays is NOT part of this body any
        // more — the backend only takes it on /machines/{id}/workdays
        // (updateMachineWorkdays below).
      };

      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(machineBody)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update machine');
      }

      const updatedMachine = await response.json();

      // Merge the response into the cached machine so separately-fetched
      // data (playlists, financialHistory, ...) is preserved.
      setMachines(prevMachines =>
        prevMachines.map(machine =>
          machine.id === machineId ? { ...machine, ...updatedMachine } : machine
        )
      );

      showSuccessNotification('Machine updated successfully.');
      return updatedMachine;
    } catch (error) {
      showErrorNotification(`Error updating machine: ${error.message}`);
      throw error;
    }
  };

  const updateOvereenkomst = async (machineId, overeenkomst) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping update.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/overeenkomst/${overeenkomst}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update plan');
      }

      setMachines(prevMachines =>
        prevMachines.map(machine =>
          machine.id === machineId ? { ...machine, overeenkomst } : machine
        )
      );

      // DJB-183: user-facing copy says Plan; the overeenkomst field/endpoint stays
      showSuccessNotification('Plan updated successfully.');
    } catch (error) {
      showErrorNotification(`Error updating plan: ${error.message}`);
      throw error;
    }
  };

  const updateDeactivateType = async (machineId, deactivateType) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping update.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/deactivate-type/${deactivateType}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update deactivate type');
      }

      const updatedMachine = await response.json();

      setMachines(prevMachines =>
        prevMachines.map(machine =>
          machine.id === machineId ? { ...machine, ...updatedMachine } : machine
        )
      );

      showSuccessNotification('Deactivate type updated successfully.');
      return updatedMachine;
    } catch (error) {
      showErrorNotification(`Error updating deactivate type: ${error.message}`);
      throw error;
    }
  };

  // DJB-166: `notify: false` lets the Config Subscriptions bulk loop show one
  // aggregated result instead of a toast per machine (same for the other
  // per-machine config assigns below)
  const updateBonusConfiguration = async (machineId, bonusConfigurationId, { notify = true } = {}) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping update.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/bonusconfiguration/${bonusConfigurationId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update bonus configuration');
      }

      const updatedBonusConfiguration = await response.json();
      
      // Update local optionalFeatures cache to reflect the new bonusId
      setOptionalFeatures(prevFeatures => {
        const existingIndex = prevFeatures.findIndex(feature => feature.machineId === machineId);
        if (existingIndex >= 0) {
          // Update existing
          const newFeatures = [...prevFeatures];
          newFeatures[existingIndex] = {
            ...newFeatures[existingIndex],
            bonusId: bonusConfigurationId
          };
          return newFeatures;
        } else {
          // Add new if it doesn't exist
          return [...prevFeatures, {
            machineId: machineId,
            bonusId: bonusConfigurationId
          }];
        }
      });
      
      if (notify) showSuccessNotification('Bonus configuration updated successfully.');
      return updatedBonusConfiguration;
    } catch (error) {
      if (notify) showErrorNotification(`Error updating bonus configuration: ${error.message}`);
      throw error;
    }
  };

  const updateLongTrackPriceMode = async (machineId, longTrackPriceModeId, { notify = true } = {}) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping update.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/longtrackpricemodes/${longTrackPriceModeId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update long track price mode');
      }

      const updatedLongTrackPriceMode = await response.json();
      
      // Update local optionalFeatures cache to reflect the new longTrackPriceModeId
      setOptionalFeatures(prevFeatures => {
        const existingIndex = prevFeatures.findIndex(feature => feature.machineId === machineId);
        if (existingIndex >= 0) {
          // Update existing
          const newFeatures = [...prevFeatures];
          newFeatures[existingIndex] = {
            ...newFeatures[existingIndex],
            longTrackPriceMode: longTrackPriceModeId
          };
          return newFeatures;
        } else {
          // Add new if it doesn't exist
          return [...prevFeatures, {
            machineId: machineId,
            longTrackPriceMode: longTrackPriceModeId
          }];
        }
      });
      
      if (notify) showSuccessNotification('Long track price mode updated successfully.');
      return updatedLongTrackPriceMode;
    } catch (error) {
      if (notify) showErrorNotification(`Error updating long track price mode: ${error.message}`);
      throw error;
    }
  };

  const updatePaymentMode = async (machineId, paymentModeId, { notify = true } = {}) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping update.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/paymentmodes/${paymentModeId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update payment mode');
      }

      // The backend answers 202 Accepted with an empty body, so nothing to parse
      if (notify) showSuccessNotification('Payment mode updated successfully.');
    } catch (error) {
      if (notify) showErrorNotification(`Error updating payment mode: ${error.message}`);
      throw error;
    }
  };

  const updateDiskPolicy = async (machineId, policyId, { notify = true } = {}) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping update.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/disk-policy/${policyId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update disk policy');
      }

      // 202 Accepted with an empty body, same as the payment mode assign
      if (notify) showSuccessNotification('Disk policy updated successfully.');
    } catch (error) {
      if (notify) showErrorNotification(`Error updating disk policy: ${error.message}`);
      throw error;
    }
  };

  // DJB-209: Accounts > Settings > Optional Features. Per optional feature,
  // how many machines sit on each sub-option - over the whole fleet, or over
  // one machine group when groupId is given (DJB-Server PR #62,
  // GET /optional-features/aggregation). Returns
  // [{ feature, totalMachines, values: [{ value, count }] }]; sub-option
  // values are text ("OFF", "8", "true"), ordered OFF, then numeric, then alphabetic
  // by the backend. No toast: the section renders the error inline.
  const fetchOptionalFeatureAggregation = async (groupId = null) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping fetch.");
      return [];
    }

    const query = groupId ? `?groupId=${encodeURIComponent(groupId)}` : '';
    const response = await fetch(
      `${API_BASE_URL}/optional-features/aggregation${query}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.status === 404) {
      throw new Error('Machine group not found');
    }
    if (!response.ok) {
      throw new Error('Failed to fetch optional feature aggregation');
    }

    return response.json();
  };

  // DJB-209: set one optional feature to a single value on every machine of
  // the fleet (no groupId) or of a machine group (PUT /optional-features/bulk).
  // The backend overwrites the previous value - a feature only ever holds one
  // value per machine - and answers { feature, value, groupId?, updatedMachines }.
  // The value travels as text ("OFF", "8", "true"), the form the backend's
  // bulk validator parses; 400 means the value is not valid for the feature.
  // The bundled optional-features cache is refreshed afterwards so the
  // per-machine form (MachineDetails) and Config Subscriptions see the new
  // values without a reload.
  const bulkApplyOptionalFeature = async (
    feature,
    value,
    groupId = null,
    { notify = true } = {}
  ) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping update.");
      return null;
    }

    try {
      const body = { feature, value: String(value) };
      if (groupId) body.groupId = groupId;

      const response = await fetch(
        `${API_BASE_URL}/optional-features/bulk`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );

      if (response.status === 404) {
        throw new Error('Machine group not found');
      }
      if (response.status === 400) {
        throw new Error(`"${value}" is not a valid value for ${feature}`);
      }
      if (!response.ok) {
        throw new Error('Failed to apply optional feature');
      }

      const result = await response.json();
      await fetchOptionalFeatures();

      if (notify) {
        showSuccessNotification(`Feature applied to ${result.updatedMachines} machines.`);
      }
      return result;
    } catch (error) {
      if (notify) showErrorNotification(`Error applying feature: ${error.message}`);
      throw error;
    }
  };

  // DJB-210: Accounts > Settings > Actions - bulk token actions (DJB-Server
  // PR #63). The fleet/group-wide twin of resetTokens/addTokens: the backend
  // fans out over every machine of the fleet (no groupId) or of one machine
  // group and applies the very same per-machine reset/gift core, in one
  // transaction (all machines or none). Both answer
  //   { action: 'RESET' | 'GIFT', scope: 'ALL' | 'GROUP', groupId?, amount?,
  //     affectedMachines, skippedMachines, machineIds, skippedMachineIds }
  // where skipped machines are group members that no longer exist. Errors
  // come back as 400 with an ExceptionDTO { message } (unknown group, amount
  // <= 0), so the message is surfaced when there is one. The section renders
  // the outcome inline, hence the notify switch (DJB-209 pattern).
  const bulkTokenEndpoint = (groupId, tail) =>
    groupId
      ? `${API_BASE_URL}/machines/groups/${encodeURIComponent(groupId)}/tokens/${tail}`
      : `${API_BASE_URL}/machines/tokens/${tail}`;

  const readErrorMessage = async (response, fallback) => {
    try {
      const body = await response.json();
      if (body && typeof body.message === 'string' && body.message.trim()) {
        return body.message;
      }
    } catch (e) {
      // no JSON body
    }
    return fallback;
  };

  const runBulkTokenAction = async (url, fallbackError) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authData.accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(await readErrorMessage(response, fallbackError));
    }

    const result = await response.json();
    return {
      ...result,
      affectedMachines: result?.affectedMachines ?? result?.machineIds?.length ?? 0,
      skippedMachines: result?.skippedMachines ?? result?.skippedMachineIds?.length ?? 0,
    };
  };

  // Reset Now for every machine in scope: each goes back to the default of
  // its own plan (POST /machines/tokens/reset or
  // POST /machines/groups/{groupId}/tokens/reset).
  const bulkResetTokens = async (groupId = null, { notify = true } = {}) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping bulk token reset.");
      return null;
    }

    try {
      const result = await runBulkTokenAction(
        bulkTokenEndpoint(groupId, 'reset'),
        'Failed to reset tokens'
      );
      if (notify) {
        showSuccessNotification(`Tokens reset on ${result.affectedMachines} machines.`);
      }
      return result;
    } catch (error) {
      if (notify) showErrorNotification(`Error resetting tokens: ${error.message}`);
      throw error;
    }
  };

  // Gift (+amount) tokens on top of the current balance of every machine in
  // scope (POST /machines/tokens/gift/{amount} or
  // POST /machines/groups/{groupId}/tokens/gift/{amount}); amount must be > 0.
  const bulkGiftTokens = async (amount, groupId = null, { notify = true } = {}) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping bulk token gift.");
      return null;
    }

    try {
      const parsed = Number(amount);
      if (!Number.isInteger(parsed) || parsed <= 0) {
        throw new Error('Gift amount must be positive');
      }
      const result = await runBulkTokenAction(
        bulkTokenEndpoint(groupId, `gift/${parsed}`),
        'Failed to gift tokens'
      );
      if (notify) {
        showSuccessNotification(`+${parsed} tokens on ${result.affectedMachines} machines.`);
      }
      return result;
    } catch (error) {
      if (notify) showErrorNotification(`Error gifting tokens: ${error.message}`);
      throw error;
    }
  };

  const clearBillingPeriod = async (machineId) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping billing clear.");
      return null;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/billing/empty`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to clear billing period');
      }

      // The backend snapshots the counters into a BillingHistory row, zeroes
      // cash/mobile/songs and keeps credits. Mirror that locally.
      const now = new Date();
      const clearedFields = {
        currentCoinDeposit: 0,
        currentIncomeFromPayconiq: 0,
        songsRequestedSince: 0,
        coinsLastEmptied: [now.getFullYear(), now.getMonth() + 1, now.getDate()],
      };

      // Refetch billing history so the just-closed period shows up as a row.
      // Keep the cached history if the refetch comes back empty (fetch error).
      const { financialHistory } = await fetchBillingHistory({ id: machineId });
      if (financialHistory.length > 0) {
        clearedFields.financialHistory = financialHistory;
      }

      setMachines(prevMachines =>
        prevMachines.map(machine =>
          machine.id === machineId ? { ...machine, ...clearedFields } : machine
        )
      );

      showSuccessNotification('Billing period cleared successfully.');
      return clearedFields;
    } catch (error) {
      showErrorNotification(`Error clearing billing period: ${error.message}`);
      throw error;
    }
  };

  const fetchTokenBalance = async (machineId) => {
    if (!authData?.accessToken) return null;

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/tokens`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch token balance');
      }

      const data = await response.json();
      return typeof data === 'object' ? data.amountTokens : data;
    } catch (error) {
      console.error(`Failed to fetch token balance for machine ${machineId}:`, error);
      return null;
    }
  };

  const resetTokens = async (machineId) => {
    if (!authData?.accessToken) return null;

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/tokens/reset`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to reset tokens');
      }

      const data = await response.json();
      showSuccessNotification('Tokens reset successfully.');
      return typeof data === 'object' ? data.amountTokens : data;
    } catch (error) {
      showErrorNotification(`Error resetting tokens: ${error.message}`);
      throw error;
    }
  };

  const addTokens = async (machineId, newBalance) => {
    if (!authData?.accessToken) return null;

    try {
      const response = await fetch(
        `${API_BASE_URL}/machines/${machineId}/tokens/set/${newBalance}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update token balance');
      }

      const data = await response.json();
      showSuccessNotification('Token balance updated successfully.');
      return typeof data === 'object' ? data.amountTokens : data;
    } catch (error) {
      showErrorNotification(`Error updating token balance: ${error.message}`);
      throw error;
    }
  };

  const fetchTokenConfigs = async () => {
    if (!authData?.accessToken) return [];

    try {
      const response = await fetch(
        `${API_BASE_URL}/token-config`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch token configs');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch token configs:', error);
      return null;
    }
  };

  const updateTokenConfig = async (overeenkomstType, amount) => {
    if (!authData?.accessToken) return null;

    try {
      const response = await fetch(
        `${API_BASE_URL}/token-config/${overeenkomstType}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ defaultTokens: amount }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update token config');
      }

      showSuccessNotification('Token config updated successfully.');
      return await response.json();
    } catch (error) {
      showErrorNotification(`Error updating token config: ${error.message}`);
      throw error;
    }
  };

  const removeFromBlacklist = async (machineId, ids) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping blacklist removal.");
      return;
    }

    try {
      // Run all deletes in parallel
      await Promise.all(ids.map(async (id) => {
        const response = await fetch(
          `${API_BASE_URL}/machines/${machineId}/blacklists/${id}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            }
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to remove blacklist id ${id}`);
        }
      }));

      // Optionally, update the local state to remove the ids from the blacklist
      setMachines(prevMachines =>
        prevMachines.map(machine =>
          machine.id === machineId
            ? { ...machine, blacklist: machine.blacklist.filter(bid => !ids.includes(bid)) }
            : machine
        )
      );

      showSuccessNotification('Selected items removed from blacklist.');
    } catch (error) {
      showErrorNotification(`Error removing from blacklist: ${error.message}`);
      throw error;
    }
  };

  const addToBlacklist = async (machineId, ids) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping blacklist addition.");
      return;
    }

    try {
      // Run all adds in parallel
      await Promise.all(ids.map(async (id) => {
        const response = await fetch(
          `${API_BASE_URL}/machines/${machineId}/blacklists/${id}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            }
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to add blacklist id ${id}`);
        }
      }));

      // Optionally, update the local state to add the ids to the blacklist
      setMachines(prevMachines =>
        prevMachines.map(machine =>
          machine.id === machineId
            ? { ...machine, blacklist: Array.from(new Set([...machine.blacklist, ...ids])) }
            : machine
        )
      );

      showSuccessNotification('Selected items added to blacklist.');
    } catch (error) {
      showErrorNotification(`Error adding to blacklist: ${error.message}`);
      throw error;
    }
  };

  // DJB-252 / DJB-Server PR #80: the open days live behind their own
  // endpoints. A 7-bool list (Monday → Sunday) is PUT as a
  // MachineWorkdaysDTO; a reset (openDays null — "Dagen niet gekend", which
  // is also what an all-closed draft normalises to, see
  // openDays.fromOpenDaysDraft) is a DELETE. Both leave the cached machine
  // with the openDays / todayStatus the backend ended up with, so the info
  // card and the map read the same thing after a save.
  const updateMachineWorkdays = async (machineId, openDays) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping open days update.");
      return null;
    }

    const reset = !Array.isArray(openDays);
    try {
      const days = reset ? null : openDays.map(Boolean);
      const todayIndex = getBrusselsTodayIndex();
      const request = reset
        ? { method: 'DELETE' }
        : {
            method: 'PUT',
            body: JSON.stringify({
              machineId,
              openDays: days,
              // computed server-side as well; sent so the body is a full DTO
              todayStatus: todayIndex >= 0 && days[todayIndex] ? 'open' : 'closed',
            }),
          };
      const response = await fetch(`${API_BASE_URL}/machines/${machineId}/workdays`, {
        ...request,
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(reset ? 'Failed to reset open days' : 'Failed to update open days');
      }

      let returned = null;
      if (!reset) {
        try {
          returned = await response.json();
        } catch (e) {
          // empty body: fall back to what was sent
        }
      }
      const workdays = {
        machineId,
        openDays: reset ? null : Array.isArray(returned?.openDays) ? returned.openDays.map(Boolean) : days,
        todayStatus: reset ? 'unknown' : normalizeTodayStatus(returned?.todayStatus),
      };

      setMachines(prevMachines =>
        prevMachines.map(machine =>
          machine.id === machineId
            ? { ...machine, openDays: workdays.openDays, todayStatus: workdays.todayStatus }
            : machine
        )
      );

      return workdays;
    } catch (error) {
      showErrorNotification(`Error updating open days: ${error.message}`);
      throw error;
    }
  };

  // DJB-247 / DJB-Server PR #80: geocoding is its own controller — the
  // generic machine PUT no longer re-geocodes. PUT .../geodata stores manual
  // coordinates (overriding whatever was geocoded), POST .../geodata/geocode
  // re-runs the geocoder on the stored address. Both answer with a
  // MachineGeodataDTO { machineId, latitude, longitude, status, geocodedAt };
  // the map itself reads /machines/map-data, so callers re-fetch that.
  const geodataRequest = async (machineId, path, options, fallbackError) => {
    const response = await fetch(`${API_BASE_URL}/machines/${machineId}/geodata${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${authData.accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, fallbackError));
    }
    try {
      return await response.json();
    } catch (e) {
      return null;
    }
  };

  const updateMachineGeodata = async (machineId, { latitude, longitude }) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping coordinates update.");
      return null;
    }

    try {
      const geodata = await geodataRequest(
        machineId,
        '',
        { method: 'PUT', body: JSON.stringify({ latitude: Number(latitude), longitude: Number(longitude) }) },
        'Failed to save coordinates'
      );
      showSuccessNotification('Coordinates saved.');
      return geodata;
    } catch (error) {
      showErrorNotification(`Error saving coordinates: ${error.message}`);
      throw error;
    }
  };

  const geocodeMachine = async (machineId) => {
    if (!authData?.accessToken) {
      console.warn("No access token found, skipping geocode.");
      return null;
    }

    try {
      return await geodataRequest(machineId, '/geocode', { method: 'POST' }, 'Failed to geocode machine');
    } catch (error) {
      showErrorNotification(`Error geocoding machine: ${error.message}`);
      throw error;
    }
  };

  useEffect(() => {
    fetchMachines();
    fetchOptionalFeatures();
    fetchBonusConfiguration();
    fetchLongTrackPriceModes();
    fetchPaymentModes();
    fetchDiskPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.accessToken]);

  return (
    <MachineContext.Provider 
      value={{ 
        machines,
        optionalFeatures,
        bonusConfiguration,
        longTrackPriceModes,
        paymentModes,
        diskPolicies,
        loading,
        error,
        fetchMachines,
        fetchOptionalFeatures,
        fetchBonusConfiguration,
        fetchLongTrackPriceModes,
        fetchPaymentModes,
        fetchDiskPolicies,
        createMachine,
        updateMachine,
        updateMachineWorkdays,
        updateMachineGeodata,
        geocodeMachine,
        updateOptionalFeatures,
        updateOvereenkomst,
        updateDeactivateType,
        updateBonusConfiguration,
        updateLongTrackPriceMode,
        updatePaymentMode,
        updateDiskPolicy,
        fetchOptionalFeatureAggregation,
        bulkApplyOptionalFeature,
        bulkResetTokens,
        bulkGiftTokens,
        toggleMachinePlaylist,
        updatePlaylistPriority,
        // DJB-214: aggregated result toast for bulk subscription runs
        showSuccessNotification,
        showErrorNotification,
        fetchCreditRequests,
        removeFromBlacklist,
        addToBlacklist,
        fetchTokenBalance,
        resetTokens,
        addTokens,
        fetchTokenConfigs,
        updateTokenConfig,
        clearBillingPeriod
      }}
    >
      {children}
      <div className="notification-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() => setNotifications((prev) => 
              prev.filter((n) => n.id !== notification.id)
            )}
          />
        ))}
      </div>
    </MachineContext.Provider>
  );
};

export default MachineProvider;